import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function CookiePolicy() {
  const { t } = useTranslation(["legalCookies", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "how-orrith-handles-storage", label: t("howOrrithHandlesClientStorage", "How Orrith Handles Client Storage") },
    { id: "cookies", label: t("cookiesSection", "Cookies") },
    { id: "local-session-storage", label: t("localStorageAndSessionStorageUse", "Local Storage and Session Storage Use") },
    { id: "analytics-marketing", label: t("analyticsAndMarketing", "Analytics and Marketing") },
    { id: "managing-browser-data", label: t("managingBrowserData", "Managing Browser Data") },
  ];

  return (
<LegalPageShell
        title={t("title", "Cookie Policy")}
        subtitle={t("subtitle", "How Orrith uses cookies and similar tracking technologies.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalCookies"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("cookiesIntro", "This Cookie Policy explains how Orrith (\"we,\" \"our,\" \"us\") uses cookies, local storage, session storage, and similar technologies when you visit orrith.me and use our web platform. This policy describes actual current behavior in the web application.")}
        </p>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("atAGlance", "At a Glance")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3 mt-4 mb-8">
          <div className="rounded-xl border border-amber-200/80 dark:border-amber-700/40 p-4 bg-amber-50/80 dark:bg-amber-900/10">
            <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-1.5">
              {t("primaryStorage", "Primary Storage")}
            </h4>
            <p className="text-sm text-amber-800/90 dark:text-amber-100/80">
              {t("primaryStorageDesc", "Orrith uses local storage and session storage for preferences, drafts, and short-lived caches.")}
            </p>
          </div>
          <div className="rounded-xl border border-amber-200/80 dark:border-amber-700/40 p-4 bg-white dark:bg-neutral-900/60">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("cookiesSection", "Cookies")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("cookiesDesc", "Orrith does not set custom marketing cookies. Firebase may set technical cookies for secure auth.")}
            </p>
          </div>
          <div className="rounded-xl border border-green-200/80 dark:border-green-700/40 p-4 bg-green-50/80 dark:bg-green-900/10">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1.5">
              {t("yourControl", "Your Control")}
            </h4>
            <p className="text-sm text-green-800/90 dark:text-green-100/80">
              {t("yourControlDesc", "You can accept, decline optional storage, or adjust it any time from the banner.")}
            </p>
          </div>
        </div>
      </section>

      <section id="how-orrith-handles-storage" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("howOrrithHandlesClientStorage", "How Orrith Handles Client Storage")}
        </h2>
        <p className="legal-prose">
          {t("clientStorageDesc", "Orrith primarily uses browser local storage and session storage for preferences, drafts, lightweight caching, and user interface continuity. We do not ship a required third-party analytics or marketing SDK in the client. You can keep analytics and marketing off without losing core features.")}
        </p>
      </section>

      <section id="cookies" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("cookiesSection", "Cookies")}
        </h2>
        <p className="legal-prose">
          {t("cookiesDetail", "Orrith provides a consent banner so you can accept, decline, or customize optional storage usage. We do not set custom marketing cookies in the React client. Firebase Authentication may set its own technical cookies (or similar storage) to keep sessions secure. These are essential for authentication and cannot be disabled without losing sign-in functionality.")}
        </p>
      </section>

      <section id="local-session-storage" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("localStorageAndSessionStorageUse", "Local Storage and Session Storage Use")}
        </h2>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("preferencesOptional", "Preferences (Optional)")}
        </h3>
        <ul className="legal-list">
          <li>{t("preferences.theme", "Theme and dark mode preference")}</li>
          <li>{t("preferences.language", "Language selection")}</li>
          <li>{t("preferences.profileCache", "Profile cache for faster navigation (non-sensitive)")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("draftsAndPerformanceOptional", "Drafts and Performance (Optional)")}
        </h3>
        <ul className="legal-list">
          <li>{t("drafts.productForms", "Product draft form data")}</li>
          <li>{t("drafts.farmPulseCache", "FarmPulse cached market snapshots (short-lived)")}</li>
          <li>{t("drafts.marketplaceStats", "Session-level marketplace stats cache")}</li>
        </ul>
      </section>

      <section id="analytics-marketing" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("analyticsAndMarketing", "Analytics and Marketing")}
        </h2>
        <p className="legal-prose">
          {t("analyticsDesc", "Orrith does not ship a required third-party analytics or marketing SDK in the client. If a deployment adds one, it should respect your consent choices. You can keep analytics and marketing off without losing core features. Any analytics implementation would be disclosed in an updated version of this policy.")}
        </p>
      </section>

      <section id="managing-browser-data" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("managingBrowserData", "Managing Browser Data")}
        </h2>
        <p className="legal-prose mb-6">
          {t("browserDataIntro", "You have control over browser-stored data:")}
        </p>
        <ul className="legal-list">
          <li>{t("browserData.clearStorage", "You can clear cookies, local storage, and session storage in browser settings")}</li>
          <li>{t("browserData.removesDrafts", "Clearing storage removes saved drafts and preferences")}</li>
          <li>{t("browserData.slowerLoad", "Some screens may load slower until caches rebuild")}</li>
          <li>{t("browserData.accountDeletion", "Deleting your Orrith account does not automatically clear browser storage on your device")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("browserInstructions", "How to Clear Storage")}
        </h3>
        <ul className="legal-list">
          <li>{t("browserInstructionsItems.chrome", "Chrome: Settings → Privacy and security → Clear browsing data → Cookies and other site data / Cached images and files")}</li>
          <li>{t("browserInstructionsItems.firefox", "Firefox: Settings → Privacy & Security → Cookies and Site Data → Clear Data")}</li>
          <li>{t("browserInstructionsItems.safari", "Safari: Preferences → Privacy → Manage Website Data → Remove All")}</li>
          <li>{t("browserInstructionsItems.edge", "Edge: Settings → Privacy, search, and services → Clear browsing data → Choose what to clear")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("policyUpdates.cookieDesc", "Storage behavior may change as product features evolve. We update this page when material changes are introduced. For questions about this policy, contact us at legal@orrith.me.")}
        </p>
      </section>
    </LegalPageShell>
  );
}